import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, Button } from '@mono/ui-components';
import { Loader2, MessageSquare, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { formatRelativeTime } from '../../utils/time';
import { FileTypeIcon } from './FileTypeIcon';
function formatFileSize(bytes) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = [
        'B',
        'KB',
        'MB',
        'GB'
    ];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Number.parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
}
const DOCUMENT_TYPE_COLORS = {
    source: {
        bg: 'bg-purple-100',
        text: 'text-purple-800',
        border: 'border-purple-300',
        label: 'Source'
    },
    amendment: {
        bg: 'bg-orange-100',
        text: 'text-orange-800',
        border: 'border-orange-300',
        label: 'Amendment'
    },
    reference: {
        bg: 'bg-blue-100',
        text: 'text-blue-800',
        border: 'border-blue-300',
        label: 'Reference'
    },
    unknown: {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        border: 'border-gray-300',
        label: 'Unknown'
    }
};
const PRIMARY_TYPE_COLORS = {
    BASE: {
        bg: 'bg-indigo-100',
        text: 'text-indigo-800',
        border: 'border-indigo-300',
        label: 'Base Document'
    },
    AMENDMENT: {
        bg: 'bg-amber-100',
        text: 'text-amber-800',
        border: 'border-amber-300',
        label: 'Amendment'
    },
    SUPPORT: {
        bg: 'bg-teal-100',
        text: 'text-teal-800',
        border: 'border-teal-300',
        label: 'Support'
    },
    ATTACHMENT: {
        bg: 'bg-cyan-100',
        text: 'text-cyan-800',
        border: 'border-cyan-300',
        label: 'Attachment'
    },
    QA: {
        bg: 'bg-emerald-100',
        text: 'text-emerald-800',
        border: 'border-emerald-300',
        label: 'Q&A'
    }
};
function formatDocumentType(documentType) {
    return documentType.replace(/_/g, ' ').split(' ').map((word)=>word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
}
function getConfidenceConfig(confidence) {
    if (confidence >= 0.9) {
        return {
            label: 'High Confidence',
            className: 'bg-green-100 text-green-700 border-green-300',
            icon: '✓'
        };
    }
    if (confidence >= 0.7) {
        return {
            label: 'Medium Confidence',
            className: 'bg-yellow-100 text-yellow-700 border-yellow-300',
            icon: '~'
        };
    }
    return {
        label: 'Low Confidence',
        className: 'bg-orange-100 text-orange-700 border-orange-300',
        icon: '?'
    };
}
function getPrimaryTypeConfig(classification) {
    if (!classification) return null;
    const primaryType = classification.primaryType.toUpperCase();
    return PRIMARY_TYPE_COLORS[primaryType] || {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        border: 'border-gray-300',
        label: classification.primaryType
    };
}
function getProcessingStatusConfig(status) {
    switch(status){
        case 'error':
            // Only show badge for errors - processing status from backend is often stale
            return {
                label: 'Error',
                className: 'bg-red-100 text-red-700 border-red-300'
            };
        default:
            return null;
    }
}
function getDocumentTypeConfig(documentType) {
    if (!documentType) return null;
    return DOCUMENT_TYPE_COLORS[documentType] || DOCUMENT_TYPE_COLORS.unknown;
}
export function ProjectFileCard({ file, onDelete, onStartChat, onClick, isCreatingChat = false, isDeleting = false, canDelete = true }) {
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const handleDelete = ()=>{
        onDelete(file.attachmentId);
        setDeleteDialogOpen(false);
    };
    const statusConfig = getProcessingStatusConfig(file.processingStatus);
    const documentTypeConfig = getDocumentTypeConfig(file.documentType);
    const classification = file.classification;
    const primaryTypeConfig = classification ? getPrimaryTypeConfig(classification) : null;
    const confidenceConfig = classification ? getConfidenceConfig(classification.confidence) : null;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "group relative bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md hover:border-gray-300 transition-all cursor-pointer",
                onClick: ()=>onClick?.(file),
                "data-testid": `file-card-${file.attachmentId}`,
                children: [
                    onStartChat && /*#__PURE__*/ _jsx(Button, {
                        variant: "ghost",
                        size: "icon",
                        className: `absolute top-2 h-7 w-7 opacity-0 group-hover:opacity-100
                       transition-opacity bg-white/80 hover:bg-blue-50 ${canDelete ? 'right-10' : 'right-2'}`,
                        onClick: (e)=>{
                            e.stopPropagation();
                            onStartChat(file);
                        },
                        disabled: isCreatingChat,
                        "aria-label": `Start chat with ${file.name}`,
                        "data-testid": `start-chat-${file.attachmentId}`,
                        children: isCreatingChat ? /*#__PURE__*/ _jsx(Loader2, {
                            className: "w-4 h-4 animate-spin text-gray-400"
                        }) : /*#__PURE__*/ _jsx(MessageSquare, {
                            className: "w-4 h-4 text-gray-400 hover:text-blue-500"
                        })
                    }),
                    canDelete && /*#__PURE__*/ _jsx(Button, {
                        variant: "ghost",
                        size: "icon",
                        className: "absolute top-2 right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity bg-white/80 hover:bg-red-50",
                        onClick: (e)=>{
                            e.stopPropagation();
                            setDeleteDialogOpen(true);
                        },
                        disabled: isDeleting,
                        "aria-label": `Delete ${file.name}`,
                        "data-testid": `delete-file-${file.attachmentId}`,
                        children: isDeleting ? /*#__PURE__*/ _jsx(Loader2, {
                            className: "w-4 h-4 animate-spin text-gray-400"
                        }) : /*#__PURE__*/ _jsx(Trash2, {
                            className: "w-4 h-4 text-gray-400 hover:text-red-500"
                        })
                    }),
                    statusConfig && /*#__PURE__*/ _jsx("div", {
                        className: `absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-medium ${statusConfig.className}`,
                        children: statusConfig.label
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex justify-center mb-3 pt-2",
                        children: /*#__PURE__*/ _jsx("div", {
                            className: "w-12 h-12 flex items-center justify-center",
                            children: /*#__PURE__*/ _jsx(FileTypeIcon, {
                                contentType: file.contentType,
                                filename: file.name,
                                className: "w-full h-full"
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm font-medium text-gray-900 truncate text-center",
                        title: file.name,
                        children: file.name
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        className: "text-xs text-gray-500 mt-1 text-center",
                        children: [
                            formatFileSize(file.size),
                            " · ",
                            formatRelativeTime(file.uploadedAt)
                        ]
                    }),
                    classification ? /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-col gap-1.5 mt-2",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "flex justify-center",
                                children: /*#__PURE__*/ _jsxs("span", {
                                    className: `px-2 py-0.5 rounded text-xs font-medium border ${classification.confidenceSource === 'filename-pattern' ? 'bg-gray-50 text-gray-500 border-gray-200 border-dashed' : 'bg-slate-100 text-slate-800 border-slate-300'}`,
                                    title: `Document Type: ${classification.documentType}${classification.confidenceSource === 'filename-pattern' ? ' (estimated from filename)' : ''}`,
                                    children: [
                                        formatDocumentType(classification.documentType),
                                        classification.confidenceSource === 'filename-pattern' && /*#__PURE__*/ _jsx("span", {
                                            className: "ml-1 text-gray-400",
                                            children: "?"
                                        })
                                    ]
                                })
                            }),
                            classification.confidenceSource !== 'filename-pattern' && primaryTypeConfig && /*#__PURE__*/ _jsx("div", {
                                className: "flex justify-center",
                                children: /*#__PURE__*/ _jsx("span", {
                                    className: `px-2 py-0.5 rounded-full text-xs font-medium border ${primaryTypeConfig.bg} ${primaryTypeConfig.text} ${primaryTypeConfig.border}`,
                                    title: `Primary Type: ${classification.primaryType}`,
                                    children: primaryTypeConfig.label
                                })
                            }),
                            classification.confidenceSource !== 'filename-pattern' && confidenceConfig && /*#__PURE__*/ _jsx("div", {
                                className: "flex justify-center",
                                children: /*#__PURE__*/ _jsxs("span", {
                                    className: `px-2 py-0.5 rounded-full text-xs font-medium border ${confidenceConfig.className}`,
                                    title: `${confidenceConfig.label}: ${(classification.confidence * 100).toFixed(0)}% (${classification.confidenceSource})`,
                                    children: [
                                        confidenceConfig.icon,
                                        " ",
                                        (classification.confidence * 100).toFixed(0),
                                        "%"
                                    ]
                                })
                            }),
                            classification.confidenceSource !== 'filename-pattern' && (classification.agency || classification.solicitationNumber) && /*#__PURE__*/ _jsx("div", {
                                className: "flex justify-center",
                                children: /*#__PURE__*/ _jsx("span", {
                                    className: "px-2 py-0.5 rounded text-xs text-gray-500 border border-gray-200 truncate max-w-full",
                                    title: [
                                        classification.agency,
                                        classification.solicitationNumber
                                    ].filter(Boolean).join(' - '),
                                    children: classification.agency || classification.solicitationNumber
                                })
                            })
                        ]
                    }) : documentTypeConfig && /*#__PURE__*/ _jsx("div", {
                        className: "flex justify-center mt-2",
                        children: /*#__PURE__*/ _jsx("span", {
                            className: `px-2 py-0.5 rounded-full text-xs font-medium border ${documentTypeConfig.bg} ${documentTypeConfig.text} ${documentTypeConfig.border}`,
                            children: documentTypeConfig.label
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(AlertDialog, {
                open: deleteDialogOpen,
                onOpenChange: setDeleteDialogOpen,
                children: /*#__PURE__*/ _jsxs(AlertDialogContent, {
                    children: [
                        /*#__PURE__*/ _jsxs(AlertDialogHeader, {
                            children: [
                                /*#__PURE__*/ _jsx(AlertDialogTitle, {
                                    children: "Delete file?"
                                }),
                                /*#__PURE__*/ _jsxs(AlertDialogDescription, {
                                    children: [
                                        'Are you sure you want to delete "',
                                        file.name,
                                        '"? This action cannot be undone.'
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(AlertDialogFooter, {
                            children: [
                                /*#__PURE__*/ _jsx(AlertDialogCancel, {
                                    children: "Cancel"
                                }),
                                /*#__PURE__*/ _jsx(AlertDialogAction, {
                                    onClick: handleDelete,
                                    className: "bg-red-600 hover:bg-red-700",
                                    children: "Delete"
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
